import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from './utils';

const URL = '/syncs';

const SyncStateSchema = z.enum(['never_synced', 'running', 'healthy', 'failing']);

export type SyncState = z.infer<typeof SyncStateSchema>;

const SyncSchema = z.object({
  kind: z.string(),
  integration: z.string(),
  integrationKey: z.string(),
  state: SyncStateSchema,
  lastSuccessAt: z.string().transform(dayjs).nullable(),
});

const SyncsResponseSchema = z.object({
  syncs: z.array(SyncSchema),
});

export type Sync = z.infer<typeof SyncSchema>;

export async function loadSyncsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = SyncsResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'syncs');
    throw new SilentError();
  }

  return parseResult.data.syncs;
}

export function useLoadSyncs(options: QueryOptions<Sync[]> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Sync[], ApiError>({
    queryKey: ['syncs'],
    queryFn: () => loadSyncsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<Sync[], ApiError, 'syncs'>('syncs', queryResult);
}
